import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * @typedef {object} UpsellPopupRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} closeButton - The close button.
 * @property {HTMLButtonElement} acceptButton - The accept (upsell) button.
 * @property {HTMLButtonElement} declineButton - The decline (original) button.
 * @property {HTMLElement} errorMessage - The error message container.
 *
 * @extends DialogComponent
 */
export class UpsellPopupComponent extends DialogComponent {
  requiredRefs = ['dialog', 'acceptButton', 'declineButton'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleTriggerClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#handleTriggerClick, true);
  }

  /**
   * Intercepts clicks on add-to-cart buttons inside the wrapped product card
   * and opens the upsell popup instead of submitting the form.
   *
   * @param {MouseEvent} event
   */
  #handleTriggerClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = target.closest('button[name="add"], add-to-cart-component button');

    if (!button) return;
    if (this.refs.dialog.contains(button)) return;

    event.preventDefault();
    event.stopPropagation();

    this.showDialog();
  };

  /**
   * Adds the upsell variant to the cart.
   *
   * @param {Event} event
   */
  acceptUpsell = (event) => {
    event.preventDefault();
    const variantId = Number(this.dataset.upsellVariantId);
    return this.#addToCart(variantId, this.refs.acceptButton);
  };

  /**
   * Adds the original variant to the cart.
   *
   * @param {Event} event
   */
  declineUpsell = (event) => {
    event.preventDefault();
    const variantId = Number(this.dataset.originalVariantId);
    return this.#addToCart(variantId, this.refs.declineButton);
  };

  /**
   * @param {number} variantId
   * @param {HTMLButtonElement} sourceButton
   */
  #addToCart = async (variantId, sourceButton) => {
    if (!variantId) {
      this.#showError('Variant introuvable.');
      return;
    }

    this.#hideError();
    this.#setLoading(sourceButton, true);

    try {
      const response = await fetch(`${Theme.routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        this.dispatchEvent(
          new CartErrorEvent(this.id || '', data.message, data.description, data.errors)
        );
        this.#showError(data.description || data.message || 'Une erreur est survenue.');
        this.#setLoading(sourceButton, false);
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, this.id || '', {
          source: 'upsell-popup-component',
          itemCount: 1,
          productId: this.dataset.productId,
        })
      );

      this.#setLoading(sourceButton, false);
      this.closeDialog();
    } catch (error) {
      console.error(error);
      this.#showError('Une erreur est survenue.');
      this.#setLoading(sourceButton, false);
    }
  };

  /**
   * @param {string} message
   */
  #showError(message) {
    const { errorMessage } = this.refs;
    if (!errorMessage) return;
    errorMessage.textContent = message;
    errorMessage.classList.remove('hidden');
  }

  #hideError() {
    const { errorMessage } = this.refs;
    if (!errorMessage) return;
    errorMessage.classList.add('hidden');
    errorMessage.textContent = '';
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} loading
   */
  #setLoading(button, loading) {
    const { acceptButton, declineButton } = this.refs;
    [acceptButton, declineButton].forEach((b) => {
      if (b) b.disabled = loading;
    });

    const text = button.querySelector('.upsell-popup__button-text');
    const loader = button.querySelector('.upsell-popup__button-loading');
    text?.classList.toggle('hidden', loading);
    loader?.classList.toggle('hidden', !loading);
  }
}

if (!customElements.get('upsell-popup-component')) {
  customElements.define('upsell-popup-component', UpsellPopupComponent);
}
